import { Link, useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowUpRight, ArrowLeft, Check, AlertTriangle } from "lucide-react";
import { PRODUCTS, CATEGORIES, INDUSTRIES } from "../lib/data";
import { CTABand } from "../components/site/Bits";
import ProductResources from "../components/site/ProductResources";
import { Reveal } from "../components/site/Reveal";
import CostBenefitChart from "../components/site/CostBenefitChart";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";

import { Seo } from "../components/site/Seo";

export default function ProductDetail() {
  const { slug } = useParams();
  const product = PRODUCTS.find((p) => p.slug === slug);

  if (!product) {
    return (
      <div className="pt-40 pb-40 text-center container-x">
        <h1 className="font-display font-bold text-3xl text-ink">Product not found</h1>
        <Link to="/solutions" className="mt-4 inline-block text-flame font-semibold">Back to products</Link>
      </div>
    );
  }

  const others = PRODUCTS.filter((p) => p.slug !== slug);

  const absImg = product.image.startsWith("http") ? product.image : `https://oneharvestco.com${product.image}`;
  const productLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.summary || product.tagline,
    image: absImg,
    category: product.category,
    brand: { "@type": "Brand", name: "OneHarvest" },
    url: `https://oneharvestco.com/products/${product.slug}`,
  };
  const faqLd = product.faqs?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: product.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;
  const jsonLd = faqLd ? [productLd, faqLd] : [productLd];

  const cat = CATEGORIES.find((c) => c.slug === product.categorySlug);
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://oneharvestco.com/" },
      ...(cat ? [{ "@type": "ListItem", position: 2, name: cat.name, item: `https://oneharvestco.com${cat.to}` }] : []),
      { "@type": "ListItem", position: cat ? 3 : 2, name: product.name, item: `https://oneharvestco.com/products/${product.slug}` },
    ],
  };
  jsonLd.push(breadcrumbLd);

  return (
    <div data-testid="product-detail-page" className="pt-[72px]">
      <Seo title={product.name} description={product.tagline} image={product.image} type="product" jsonLd={jsonLd} />
      <section className="bg-ink">
        <div className="container-x py-16">
          <Link to={CATEGORIES.find((c) => c.slug === product.categorySlug)?.to || "/"} className="inline-flex items-center gap-2 text-white/60 hover:text-flame text-sm font-medium mb-10">
            <ArrowLeft className="h-4 w-4" /> {CATEGORIES.find((c) => c.slug === product.categorySlug)?.name || "Back"}
          </Link>
          <div className="grid gap-12 lg:grid-cols-12 items-center">
            <Reveal className="lg:col-span-6">
              <div className="relative">
                <div className="absolute -inset-3 bg-flame/10" />
                <img src={product.image} alt={product.name} className={`relative w-full border border-white/10 ${product.fit === "contain" ? "object-contain bg-white max-h-[460px]" : "object-cover"}`} />
              </div>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-6">
              <div className="kicker text-flame">{product.category}</div>
              <h1 className="mt-3 font-display font-black text-white text-4xl md:text-6xl tracking-tight">{product.name}</h1>
              <p className="mt-5 text-white/80 text-lg leading-relaxed">{product.tagline}</p>
              <Link to="/request-quote" data-testid="product-quote-cta" className="mt-8 inline-flex items-center gap-2 bg-flame text-white px-6 py-4 font-semibold hover:bg-flame/90 transition-colors">
                Request a Quote <ArrowUpRight className="h-4 w-4" />
              </Link>
              {product.slug === "big-bad-mats" && (
                <div data-testid="single-source-strip" className="mt-8 flex flex-wrap gap-x-6 gap-y-3 border-t border-white/10 pt-6">
                  {[
                    { icon: "Layers", label: "Any mat" },
                    { icon: "Factory", label: "Any manufacturer" },
                    { icon: "Repeat", label: "Buy or turnkey" },
                    { icon: "UserRound", label: "One point of contact" },
                  ].map((item) => {
                    const Icon = Icons[item.icon] || Icons.Check;
                    return (
                      <div key={item.label} className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-flame flex-shrink-0" />
                        <span className="text-white/80 text-sm font-medium">{item.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-background">
        <div className="container-x py-24 grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <div className="kicker text-flame mb-2">
              <Link to={CATEGORIES.find((c) => c.slug === product.categorySlug)?.to || "/"} className="hover:text-flame/80">
                {CATEGORIES.find((c) => c.slug === product.categorySlug)?.name}
              </Link>
            </div>
            <h2 className="font-display font-bold text-3xl text-ink">Overview</h2>
            <p className="mt-5 text-muted-foreground text-lg leading-relaxed">{product.summary}</p>

            <h3 className="mt-10 font-display font-bold text-2xl text-ink">Use cases</h3>
            <div className="mt-4 flex flex-wrap gap-2">
              {product.useCases.map((u) => (
                <span key={u} className="text-sm bg-muted text-charcoal px-3 py-1.5 border border-border">{u}</span>
              ))}
            </div>

            <h3 className="mt-10 font-display font-bold text-2xl text-ink">Key advantages</h3>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {product.highlights.map((h) => (
                <div key={h} className="flex items-start gap-3 border border-border bg-card p-5">
                  <Check className="h-5 w-5 text-flame flex-shrink-0 mt-0.5" />
                  <span className="text-charcoal">{h}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-border bg-card p-8 sticky top-24">
              <h3 className="font-display font-bold text-xl text-ink">Common applications</h3>
              <ul className="mt-5 space-y-3">
                {product.applications.map((a) => (
                  <li key={a} className="flex items-center gap-3 text-charcoal">
                    <span className="h-1.5 w-1.5 bg-flame" /> {a}
                  </li>
                ))}
              </ul>
              <div className="mt-8 pt-6 border-t border-border">
                <p className="kicker text-muted-foreground mb-3">Sectors served</p>
                <div className="flex flex-wrap gap-2">
                  {product.sectors.map((s) => {
                    const sec = INDUSTRIES.find((i) => i.slug === s);
                    if (!sec) return null;
                    return (
                      <Link key={s} to={`/industries/${s}`} className="text-xs border border-border px-2.5 py-1 text-charcoal hover:border-flame hover:text-flame transition-colors">
                        {sec.name}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {product.slug === "big-bad-mats" && (
        <section data-testid="brief-banner" className="bg-ink">
          <div className="container-x py-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <Icons.Gauge className="h-10 w-10 text-flame flex-shrink-0" />
              <div>
                <div className="kicker text-flame">Executive Procurement Brief</div>
                <h3 className="mt-1 font-display font-bold text-xl md:text-2xl text-white">Cost Benefit &amp; Unit Economics Analysis</h3>
                <p className="text-white/60 text-sm mt-1">A board-ready analysis for procurement, operations & finance leaders.</p>
              </div>
            </div>
            <Link to="/big-bad-mats-procurement-brief" data-testid="view-brief-cta" className="inline-flex items-center gap-2 bg-flame text-white px-6 py-4 font-semibold hover:bg-flame/90 transition-colors whitespace-nowrap">
              View the Brief <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      )}

      {product.costBenefit && (
        <section data-testid="cost-benefit-section" className="bg-background">
          <div className="container-x py-20">
            <Reveal>
              <CostBenefitChart data={product.costBenefit} />
            </Reveal>
          </div>
        </section>
      )}

      {product.spotlight && (
        <section data-testid="spotlight-section" className="bg-background">
          <div className="relative min-h-[44vh] md:h-[44vh] overflow-hidden">
            <img src={product.spotlight.heroImage} alt={product.name} className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-ink/95 via-ink/70 to-ink/25" />
            <div className="relative container-x h-full flex flex-col justify-center py-12 md:py-0">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 border border-flame/60 bg-flame/10 text-flame px-3 py-2 kicker">
                  <Icons.BadgeCheck className="h-4 w-4" /> {product.spotlight.badge}
                </div>
                <h2 className="mt-4 font-display font-extrabold text-white text-3xl md:text-4xl tracking-tight text-balance">{product.spotlight.tagline}</h2>
              </div>
            </div>
          </div>

          <div className="border-b border-border bg-card">
            <div className="container-x py-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
              {product.spotlight.iconHighlights.map((h) => {
                const Icon = Icons[h.icon] || Icons.CheckCircle2;
                return (
                  <div key={h.label} className="flex flex-col items-center text-center gap-2">
                    <Icon className="h-7 w-7 text-flame" />
                    <span className="text-xs font-semibold text-charcoal uppercase tracking-wide">{h.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="container-x py-24 grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <p className="text-muted-foreground text-lg leading-relaxed">{product.spotlight.intro}</p>
              <h3 className="mt-10 font-display font-bold text-2xl text-ink">Designed for the fire service</h3>
              <p className="mt-3 text-muted-foreground leading-relaxed">{product.spotlight.designedFor}</p>

              <h3 className="mt-10 font-display font-bold text-2xl text-ink">Features &amp; benefits</h3>
              <div className="mt-5 space-y-3">
                {product.spotlight.features.map((f) => (
                  <div key={f} className="flex items-start gap-3">
                    <Icons.Check className="h-5 w-5 text-flame flex-shrink-0 mt-0.5" />
                    <span className="text-charcoal text-sm leading-relaxed">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 space-y-6">
              <div className="border border-border bg-card p-7">
                <h3 className="font-display font-bold text-xl text-ink">Ideal applications</h3>
                <div className="mt-4 flex flex-wrap gap-2">
                  {product.spotlight.applications.map((a) => (
                    <span key={a} className="text-sm bg-muted text-charcoal px-3 py-1.5 border border-border">{a}</span>
                  ))}
                </div>
                {product.spotlight.applicationsNote && <p className="mt-4 text-xs text-muted-foreground italic">{product.spotlight.applicationsNote}</p>}
              </div>
              <div className="border border-border bg-ink text-white p-7">
                <h3 className="font-display font-bold text-xl">Why fire departments choose it</h3>
                <ul className="mt-4 space-y-3">
                  {product.spotlight.whyChoose.map((w) => (
                    <li key={w} className="flex items-start gap-3 text-sm text-white/80">
                      <Icons.ShieldCheck className="h-4 w-4 text-flame flex-shrink-0 mt-0.5" /> {w}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-charcoal">
            <div className="container-x py-16 text-center max-w-3xl mx-auto">
              <h3 className="font-display font-extrabold text-2xl md:text-3xl text-white">Protect your PPE. Protect your firefighters.</h3>
              <p className="mt-4 text-white/70 leading-relaxed">{product.spotlight.closing}</p>
              <Link to="/request-quote" data-testid="spotlight-quote-cta" className="mt-8 inline-flex items-center gap-2 bg-flame text-white px-6 py-4 font-semibold hover:bg-flame/90 transition-colors">
                Request product info &amp; pricing <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {product.premierLine && (
        <section data-testid="premier-line-section" className="bg-muted">
          <div className="container-x py-24">
            <div className="max-w-3xl">
              <div className="kicker text-flame mb-3">{product.premierLine.kicker || "Product Line"}</div>
              <h2 className="font-display font-extrabold text-3xl md:text-4xl text-ink tracking-tight">{product.premierLine.title}</h2>
              <p className="mt-4 text-muted-foreground text-lg leading-relaxed">{product.premierLine.intro}</p>
            </div>

            <div className="mt-10 kicker text-charcoal">{product.premierLine.forSaleLabel || `The ${product.name} line`}</div>

            {/* Desktop table */}
            <div className="mt-4 hidden lg:block border border-border bg-card">
              <div className="grid grid-cols-12 bg-ink text-white text-xs uppercase tracking-widest">
                <div className="col-span-3 p-4 border-r border-white/10">Product</div>
                <div className="col-span-4 p-4 border-r border-white/10">Best-fit use case</div>
                <div className="col-span-5 p-4">When to recommend</div>
              </div>
              {product.premierLine.products.map((row, i) => (
                <div key={row.name} className={`grid grid-cols-12 ${i % 2 ? "bg-muted/40" : "bg-card"} border-t border-border`}>
                  <div className="col-span-3 p-4 border-r border-border">
                    <span className="font-display font-bold text-ink">{row.name}</span>
                  </div>
                  <div className="col-span-4 p-4 border-r border-border text-sm text-charcoal leading-relaxed">{row.bestFit}</div>
                  <div className="col-span-5 p-4 text-sm text-muted-foreground leading-relaxed">{row.react}</div>
                </div>
              ))}
            </div>

            {/* Mobile cards */}
            <div className="mt-4 grid gap-4 lg:hidden">
              {product.premierLine.products.map((row) => (
                <div key={row.name} className="border border-border bg-card p-6">
                  <h3 className="font-display font-bold text-xl text-ink">{row.name}</h3>
                  <div className="mt-3">
                    <div className="kicker text-flame">Best-fit use case</div>
                    <p className="mt-1 text-sm text-charcoal leading-relaxed">{row.bestFit}</p>
                  </div>
                  <div className="mt-3">
                    <div className="kicker text-muted-foreground">When to recommend</div>
                    <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{row.react}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 grid gap-6 md:grid-cols-2">
              <div className="border-l-2 border-flame bg-card p-6">
                <div className="kicker text-flame mb-2">Packaging</div>
                <p className="text-sm text-charcoal leading-relaxed">{product.premierLine.packaging}</p>
              </div>
              <div className="border-l-2 border-ink bg-card p-6">
                <div className="kicker text-ink mb-2">How we recommend</div>
                <p className="text-sm text-charcoal leading-relaxed">{product.premierLine.closing}</p>
              </div>
            </div>

            <Link to="/request-quote" data-testid="premier-quote-cta" className="mt-8 inline-flex items-center gap-2 bg-flame text-white px-6 py-4 font-semibold hover:bg-flame/90 transition-colors">
              Get a tailored recommendation <ArrowUpRight className="h-4 w-4" />
            </Link>

            {product.premierLine.disposal && (
              <div data-testid="bonedry-disposal-notice" className="mt-10 flex items-start gap-4 border border-flame/40 bg-flame/5 p-6">
                <AlertTriangle className="h-6 w-6 text-flame flex-shrink-0 mt-0.5" />
                <div>
                  <div className="kicker text-flame mb-1">Disposal & Compliance Notice</div>
                  <p className="text-sm text-charcoal leading-relaxed">{product.premierLine.disposal}</p>
                </div>
              </div>
            )}
          </div>
        </section>
      )}


      <ProductResources slug={product.slug} productName={product.name} />

      {product.faqs?.length > 0 && (
        <section className="bg-background border-t border-border" data-testid="product-faq">
          <div className="container-x py-20">
            <div className="kicker text-flame">FAQ</div>
            <h2 className="mt-2 font-display font-bold text-3xl text-ink">Frequently asked questions</h2>
            <Accordion type="single" collapsible className="mt-8 max-w-3xl">
              {product.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`product-faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-semibold text-ink hover:text-flame">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed text-base">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      <section className="bg-charcoal">
        <div className="container-x py-20">
          <h2 className="font-display font-bold text-2xl text-white mb-10">More from our portfolio</h2>
          <div className="grid gap-6 md:grid-cols-2">
            {others.map((p) => (
              <Link key={p.slug} to={`/products/${p.slug}`} className="group relative flex items-center gap-6 bg-ink border border-white/10 hover:border-flame/50 transition-colors p-4 overflow-hidden">
                <div className="absolute top-0 left-0 z-20 h-1 w-0 bg-flame group-hover:w-full transition-all duration-500 ease-out" />
                <img src={p.image} alt={p.name} className="h-24 w-24 object-cover flex-shrink-0 group-hover:scale-105 transition-transform duration-500" />
                <div>
                  <div className="kicker text-flame">{p.category}</div>
                  <h3 className="mt-1 font-display font-bold text-xl text-white group-hover:text-flame transition-colors">{p.name}</h3>
                  <span className="mt-1 inline-flex items-center gap-1 text-flame text-sm">View <ArrowUpRight className="h-3 w-3" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
